import { Attributes } from '../models/attributes.model';
import { observable, action, runInAction, computed, makeObservable } from 'mobx';

import storageService from '../services/storage.service';
import ISession from '../models/session.model';
import ILog, { IRequestEvent, LogType, RequestEventPhase, RequestKind, RequestOutcome } from '../models/log.model';
import {SearchParam, SearchType, RangeMode} from '../models/search.model';
import SearchStore from './search.store';
import CommonStore from './common.store';
import Popup from '../components/common/popup.component';
import logConnection from '../services/connection/log.connection';
import { IBucket } from '../models/ bucket.model';
import moment from 'moment';
import memberStore from './member.store';
import { durationToInterval } from '../components/common/time_range_buttons.component';
import { getLogger } from '../services/logger.service';

const logger = getLogger('LogsStore');

export enum PagingType {
  NONE,
  NEXT, 
  PREV
}

export enum ItemType {
  SESSION = 0,
  MESSAGE,
  EXCEPTION,
  EVENT,
  LOADING,
  REQUEST
}

const LIMIT = 200;

// A request as its loaded start/end events describe it; the end carries everything, the start only what's known at start.
export interface RequestBlock {
  requestId: string;
  app: string;
  kind?: RequestKind;
  method?: string;
  route?: string;
  operation?: string;
  time: Date; // start
  status?: number;
  outcome?: RequestOutcome;
  errorCode?: number;
  duration?: number;
  path?: string;
  ip?: string;
  userAgent?: string;
  attributes?: Attributes;
  parallel: boolean;
  collapsed: boolean;
}

export interface Item {
  itemType: ItemType;
  data?: ILog | ISession;
  lane?: number; // flow only: backend app lane; undefined = the flow's app session
  request?: RequestBlock;
}

export class LogsStore {
  sessions: ISession[] = [];
  buckets: IBucket[] = [];
  get logCount() {
    return this.buckets.map(bucket => bucket.doc_count).reduce((a, b) => a + b, 0);
  }

  messagePopupIndex = 0;

  isLoglytics = false;

  logsCounter = 0;
  
  get itemList() {
    // The mode comes from the loaded data, not the URL: right after navigating, the URL is new but the data still the previous page's.
    const itemList: Item[] = this.isFlowData ? this.flowItems() : this.sessionItems();
    logger.v('the item list length ' + itemList.length);
    if (this.nextCursor && itemList.length > LIMIT) itemList.push({ itemType: ItemType.LOADING });
    return itemList;
  }

  // Backend apps of the flow, in a stable order so each keeps its lane color across pages.
  get flowLanes() {
    const backendApps = [...new Set(this.sessions.filter(session => session.callerSessionId).map(session => session.app))].sort();
    return new Map(backendApps.map((app, lane) => [app, lane]));
  }

  // In start order, one pass per app finds overlaps: a request overlaps the open one that ends latest.
  get requestBlocks() {
    const rows = this.isFlowData ? this.flowRows : this.sessions.flatMap(session => session.logs as ILog[]);
    const blocks = new Map<string, RequestBlock>();
    for (const event of rows.filter(row => row.type === LogType.REQUEST_EVENT) as IRequestEvent[]) {
      const block = blocks.get(event.requestId!) ?? { requestId: event.requestId!, app: event.app, time: event.time, parallel: false,
        collapsed: this.collapsedRequests.has(event.requestId!) };
      const { kind, method, route, operation, path, ip, userAgent, attributes } = event;
      Object.assign(block, { kind, method, route, operation, path, ip, userAgent, attributes });
      if (event.event === RequestEventPhase.END) {
        const { startTime, status, outcome, errorCode, duration } = event;
        Object.assign(block, { time: startTime ?? block.time, status, outcome, errorCode, duration });
      }
      blocks.set(event.requestId!, block);
    }
    const latest = new Map<string, { block: RequestBlock, end: number }>();
    for (const block of [...blocks.values()].sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())) {
      const start = new Date(block.time).getTime();
      const end = start + (block.duration ?? 0);
      const open = latest.get(block.app);
      if (open && start < open.end) block.parallel = open.block.parallel = true;
      if (!open || end > open.end) latest.set(block.app, { block, end });
    }
    return blocks;
  }

  private logItemType(log: ILog) {
    switch (log.type) {
      case LogType.MESSAGE: return ItemType.MESSAGE;
      case LogType.EXCEPTION: return ItemType.EXCEPTION;
      case LogType.ACTION_EVENT:
      case LogType.SCREEN_EVENT:
      case LogType.ACTIVITY_EVENT:
      case LogType.APP_EVENT:
      case LogType.CONFIG_EVENT:
      case LogType.REQUEST_EVENT:
        return ItemType.EVENT;
      default: return undefined;
    }
  }

  // A request's rows arrive together (server sort), so a header goes where the requestId changes; start/end show as rows only with events on.
  private rowItems(rows: ILog[], lanes = new Map<string, number>()) {
    const blocks = this.requestBlocks;
    const items: Item[] = [];
    let current: RequestBlock | undefined;
    for (const row of rows) {
      const lane = lanes.get(row.app);
      if (row.requestId && row.requestId !== current?.requestId) {
        current = blocks.get(row.requestId) ?? { requestId: row.requestId, app: row.app, time: row.requestTime ?? row.time, parallel: false,
          collapsed: this.collapsedRequests.has(row.requestId) };
        items.push({ itemType: ItemType.REQUEST, request: current, lane });
      }
      const request = row.requestId ? current : undefined;
      const itemType = this.logItemType(row);
      if (request?.collapsed || itemType === undefined || (row.type === LogType.REQUEST_EVENT && !SearchStore.showEvents)) continue;
      items.push({ itemType, data: row, lane, request });
    }
    return items;
  }

  private sessionItems() {
    return this.sessions.flatMap(session => [{ itemType: ItemType.SESSION, data: session }, ...this.rowItems(session.logs as ILog[])]);
  }

  private flowItems() {
    return this.rowItems(this.flowRows, this.flowLanes);
  }

  minMode: boolean = storageService.getItem('minMode', false);


  isLoading = false;
  nextCursor?: string = undefined;

  // Backend sessions linked to an app session (one per backend app), fetched lazily for the flow banner
  linkedApps = new Map<string, {app: string, sessionId: string}[]>();

  collapsedRequests = new Set<string>();

  flowRows: ILog[] = [];
  isFlowData = false;

  constructor () {
    makeObservable(this, {
      sessions: observable,
      linkedApps: observable,
      collapsedRequests: observable,
      flowRows: observable,
      isFlowData: observable,
      flowLanes: computed,
      requestBlocks: computed,
      toggleRequest: action,
      buckets: observable,
      logCount: computed,
      messagePopupIndex: observable,
      isLoglytics: observable,
      logsCounter: observable,
      itemList: computed,
      minMode: observable,
      nextCursor: observable,
      getLogs: action,
      loadLinkedApps: action,
      setMinMode: action,
      setMessagePopupIndex: action,
      setIsLoglytics: action,
      clear: action
    });

    this.getLogs = this.getLogs.bind(this);
  }

  private defaultFromForRange(range: RangeMode | undefined, retentionDays: number): moment.Moment {
    switch (range) {
      case RangeMode.QUARTER_HOUR: return moment().subtract(15, 'minutes');
      case RangeMode.HOUR: return moment().subtract(1, 'hour');
      case RangeMode.DAY: return moment().subtract(24, 'hours');
      case RangeMode.WEEK: return moment().subtract(7, 'days');
      case RangeMode.ALL: return moment().subtract(retentionDays, 'days');
      default: return moment().subtract(1, 'hour');
    }
  }

  async getLogs(hasLoader: boolean, pagingType: PagingType = PagingType.NONE) {
    logger.v('getLogs');
    if (this.isLoading) return;
    this.isLoading = true;
    if (hasLoader) CommonStore.setLoading(true);
    if (pagingType === PagingType.NONE) {
      this.sessions = [];
      this.flowRows = [];
    }
    try {
      const data = { ...SearchStore.logsParams };

      // checking interval for time
      logger.v(`param from time: ${data[SearchParam.FROM_TIME]} to time: ${data[SearchParam.TO_TIME]}`);
      const retentionDays = memberStore.currentPlan?.retention ?? 7;
      const fromTime = data[SearchParam.FROM_TIME]
        ? moment(new Date(data[SearchParam.FROM_TIME]))
        : this.defaultFromForRange(SearchStore.range, retentionDays);
      const toTime = data[SearchParam.TO_TIME] ? moment(new Date(data[SearchParam.TO_TIME])) : moment();
      data[SearchParam.FROM_TIME] = fromTime.toISOString();
      data[SearchParam.TO_TIME] = toTime.toISOString();
      logger.v(`from time: ${fromTime} to time: ${toTime}`);

      const interval = durationToInterval(toTime.diff(fromTime));
      logger.v(`interval: ${interval}`);
      data.interval = interval;
      data.histogram = true;

      // limit
      const limit = LIMIT;
      data.limit = limit;
      if (pagingType === PagingType.NEXT) {
        if (!this.nextCursor) {
          // No cursor available - cannot paginate
          return;
        }
        data[SearchParam.SEARCH_AFTER] = this.nextCursor;
      }
      else {
        this.nextCursor = undefined;
        delete data[SearchParam.SEARCH_AFTER];
      }
      
      const {sessions, logs, buckets, nextCursor} = await logConnection.getSessionLogs(data);
      logger.d('the session received', sessions);

      runInAction(() => {
        this.nextCursor = nextCursor;
        this.buckets = buckets ?? [];
        switch (pagingType) {
          case PagingType.NONE: 
            this.sessions = sessions;
            this.flowRows = logs ?? [];
            this.isFlowData = !!logs;
            this.logsCounter++;
            break;
          case PagingType.NEXT: {
            if (sessions.length <= 0) break;
            if (this.isFlowData) {
              this.flowRows.push(...(logs ?? []));
              this.sessions.push(...sessions.filter(session => !this.sessions.some(s => s._id === session._id)));
              break;
            }
            const lastIndex = this.sessions.length - 1;
            if (sessions[0]._id === this.sessions[lastIndex]._id) {
              const firstSession = sessions.shift();
              (this.sessions[lastIndex].logs as ILog[]).push(...(firstSession!.logs as ILog[]));
            }
            this.sessions.push(...sessions);
            break;
          }
          default:
            break;

        }
      });
    } catch (error) {
      Popup.createError({msg: 'Had a problem loading the logs'}); 
      logger.e('error loading logs', error);
    } finally {
      this.isLoading = false;
      if (hasLoader) CommonStore.setLoading(false);
    }
  }

  async loadLinkedApps(sessionId: string) {
    if (this.linkedApps.has(sessionId)) return;
    this.linkedApps.set(sessionId, []); // mark as in-flight so the sticky panel doesn't refetch
    const linked = await logConnection.getLinkedApps(sessionId);
    runInAction(() => this.linkedApps.set(sessionId, linked));
  }

  toggleRequest(requestId: string) {
    if (this.collapsedRequests.has(requestId)) this.collapsedRequests.delete(requestId);
    else this.collapsedRequests.add(requestId);
  }

  setMinMode(minMode: boolean) {
    this.minMode = minMode;
    storageService.setItem('minMode', minMode);
  }

  setMessagePopupIndex(messagePopupIndex: number) {
    this.messagePopupIndex = messagePopupIndex;
  }

  setIsLoglytics(isLoglytics: boolean) {
    this.isLoglytics = isLoglytics;
  }

  clear() {
    this.sessions = [];
    this.flowRows = [];
    this.isFlowData = false;
    this.nextCursor = undefined;
  }
}

export default new LogsStore();  